import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Building2, Search, Edit, Trash2, Star,
  MapPin, Layers, MessageSquare,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, StatsCard, EmptyState, AppButton, ConfirmModal,
} from "../ui";
import { useBranch } from "../../contexts/BranchContext";
import "./branches.scss";

const BranchesPage = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [defaultTarget, setDefaultTarget] = useState(null);
  const navigate = useNavigate();
  const { refetchBranches } = useBranch();

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/branches", { params: { includeInactive: true } });
      setBranches(data.branches || []);
    } catch {
      setToast({ show: true, message: "Failed to load branches.", type: "error" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchBranches(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return branches;
    const q = search.toLowerCase();
    return branches.filter(b =>
      b.name?.toLowerCase().includes(q) ||
      b.code?.toLowerCase().includes(q) ||
      b.city?.toLowerCase().includes(q)
    );
  }, [branches, search]);

  const stats = useMemo(() => ({
    total: branches.length,
    active: branches.filter(b => b.isActive).length,
    totalBatches: branches.reduce((s, b) => s + (b.batchCount || 0), 0),
    totalEnquiries: branches.reduce((s, b) => s + (b.enquiryCount || 0), 0),
  }), [branches]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/branches/${deleteTarget._id}`);
      setToast({ show: true, message: "Branch deactivated.", type: "success" });
      setDeleteTarget(null);
      fetchBranches();
      refetchBranches();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to delete branch.", type: "error" });
      setDeleteTarget(null);
    }
  };

  const handleSetDefault = async () => {
    if (!defaultTarget) return;
    try {
      await apiClient.patch(`/branches/${defaultTarget._id}/set-default`);
      setToast({ show: true, message: `"${defaultTarget.name}" is now the default branch.`, type: "success" });
      setDefaultTarget(null);
      fetchBranches();
      refetchBranches();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || "Failed to set default.", type: "error" });
      setDefaultTarget(null);
    }
  };

  return (
    <div className="branches-page">
      <CommonToaster toast={toast} onClose={() => setToast({ ...toast, show: false })} />

      <PageHeader
        icon={Building2}
        title="Branches"
        description="Manage your institute branches"
        actions={
          <AppButton onClick={() => navigate("/branches/add")}>
            <Plus size={16} /> Add Branch
          </AppButton>
        }
      />

      {/* Stats */}
      <div className="branches-stats">
        <StatsCard title="Total Branches" value={stats.total} icon={Building2} color="blue" />
        <StatsCard title="Active" value={stats.active} icon={MapPin} color="green" />
        <StatsCard title="Total Batches" value={stats.totalBatches} icon={Layers} color="purple" />
        <StatsCard title="Total Enquiries" value={stats.totalEnquiries} icon={MessageSquare} color="orange" />
      </div>

      {/* Toolbar */}
      <div className="branches-toolbar">
        <div className="branches-search">
          <Search size={16} className="branches-search__icon" />
          <input
            className="branches-search__input"
            placeholder="Search branches..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="branches-toolbar__spacer" />
      </div>

      {/* Table */}
      {loading ? (
        <div className="branches-loading"><div className="spinner-border" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Building2 size={40} />}
          title={search ? "No branches match your search" : "No branches yet"}
          description={search ? "Try a different search term." : "Create your first branch to get started."}
          action={!search ? <AppButton onClick={() => navigate("/branches/add")}><Plus size={16} /> Add Branch</AppButton> : undefined}
        />
      ) : (
        <div className="branches-table-wrap">
          <table className="branches-table">
            <thead>
              <tr>
                <th>Branch</th>
                <th>Code</th>
                <th>City</th>
                <th>Batches</th>
                <th>Enquiries</th>
                <th>Enrollments</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b._id}>
                  <td>
                    <div className="branch-name-cell">
                      {b.name}
                      {b.isDefault && (
                        <span className="branch-default-badge">
                          <Star size={10} /> Default
                        </span>
                      )}
                    </div>
                  </td>
                  <td><span className="branch-code">{b.code || "—"}</span></td>
                  <td>{b.city || "—"}</td>
                  <td><span className="branch-count-badge">{b.batchCount || 0}</span></td>
                  <td><span className="branch-count-badge">{b.enquiryCount || 0}</span></td>
                  <td><span className="branch-count-badge">{b.enrollmentCount || 0}</span></td>
                  <td>
                    <span className={`branch-count-badge ${b.isActive ? '' : 'text-danger'}`}>
                      {b.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="branch-actions">
                      <button title="Edit" onClick={() => navigate(`/branches/edit/${b._id}`)}>
                        <Edit size={15} />
                      </button>
                      {!b.isDefault && (
                        <button className="branch-action--star" title="Set as default" onClick={() => setDefaultTarget(b)}>
                          <Star size={15} />
                        </button>
                      )}
                      {!b.isDefault && (
                        <button className="branch-action--danger" title="Deactivate" onClick={() => setDeleteTarget(b)}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirm: Delete */}
      <ConfirmModal
        show={!!deleteTarget}
        title="Deactivate Branch"
        message={`Are you sure you want to deactivate "${deleteTarget?.name}"? Batches must be reassigned first.`}
        confirmLabel="Deactivate"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Confirm: Set Default */}
      <ConfirmModal
        show={!!defaultTarget}
        title="Set Default Branch"
        message={`Set "${defaultTarget?.name}" as the default branch for this institute?`}
        confirmLabel="Set Default"
        onConfirm={handleSetDefault}
        onCancel={() => setDefaultTarget(null)}
      />
    </div>
  );
};

export default BranchesPage;
